import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { colors, fonts, radii, shadows, zIndex, breakpoints } from './tokens';
import { Button } from './Button';
import { IconButton } from './IconButton';
import { CloseIcon, PencilIcon, TrashIcon, DocumentIcon } from './icons';
import { useSessionUI } from './Dialog';
import { useSessionNotes } from './useSessionNotes';

// Notes drawer ("See Notes" / "Add Notes"). No Figma frame exists: right-side drawer in the
// session language (light-teal header strip, white cards, teal primary). Full screen on phones.

const slideIn = keyframes`from{transform:translateX(100%)}to{transform:none}`;
const fade = keyframes`from{opacity:0}to{opacity:1}`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.drawer};
  background: ${colors.scrim};
  animation: ${fade} 120ms ease-out;
`;

const Drawer = styled.aside`
  position: fixed;
  z-index: ${zIndex.drawer + 1};
  top: 0;
  right: 0;
  bottom: 0;
  width: min(440px, 100vw);
  display: flex;
  flex-direction: column;
  background: #fff;
  box-shadow: ${shadows.dialog};
  font-family: ${fonts.family};
  color: ${colors.text};
  animation: ${slideIn} 180ms ease-out;
  @media (max-width: ${breakpoints.phone - 1}px) {
    width: 100vw;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid ${colors.slate[200]};
  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: ${colors.slate[700]};
    flex: 1;
  }
  .sub {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: ${colors.textMuted};
  }
`;

const List = styled.div`
  flex: 1;
  overflow: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${colors.slate[50]};
`;

const NoteCard = styled.article`
  background: #fff;
  border: 1px solid ${colors.slate[200]};
  border-radius: ${radii.card}px;
  padding: 12px 14px;
  ${({ legacy }) => (legacy ? `background:${colors.lightTeal};border-color:${colors.brand[100]};` : '')}
  header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 12px;
    color: ${colors.textMuted};
  }
  .avatar {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${colors.avatar};
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .who {
    font-weight: 700;
    color: ${colors.slate[700]};
  }
  .tag {
    padding: 1px 6px;
    border-radius: 2px;
    background: ${colors.brand[50]};
    color: ${colors.primary};
    font-weight: 700;
    font-size: 10px;
  }
  .actions {
    margin-left: auto;
    display: flex;
    gap: 2px;
  }
  p {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 14px;
    line-height: 1.55;
    user-select: text;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: ${({ rows = 4 }) => rows * 22}px;
  box-sizing: border-box;
  padding: 10px 12px;
  border: 1px solid ${colors.slate[200]};
  border-radius: ${radii.input}px;
  resize: vertical;
  font: 400 14px/1.5 ${fonts.family};
  color: ${colors.text};
  background: #fff;
  user-select: text;
  &:focus {
    outline: 2px solid ${colors.brand[300]};
    border-color: ${colors.brand[400]};
  }
`;

const Composer = styled.div`
  padding: 14px 20px 20px;
  border-top: 1px solid ${colors.slate[200]};
  display: flex;
  flex-direction: column;
  gap: 10px;
  .row {
    display: flex;
    align-items: center;
    gap: 12px;
    justify-content: space-between;
  }
  label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${colors.slate[600]};
  }
  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    accent-color: ${colors.primary};
  }
  .hint {
    font-size: 12px;
    color: ${colors.textMuted};
  }
`;

const Empty = styled.div`
  margin: auto;
  text-align: center;
  color: ${colors.textMuted};
  font-size: 14px;
  svg {
    color: ${colors.slate[300]};
    margin-bottom: 8px;
  }
`;

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
};

const useDebounced = (fn, delay) => {
  const t = useRef(null);
  useEffect(() => () => clearTimeout(t.current), []);
  return (...args) => {
    clearTimeout(t.current);
    t.current = setTimeout(() => fn(...args), delay);
  };
};

const EditableNote = ({ note, canEdit, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.body || '');
  const [saveState, setSaveState] = useState('');
  const save = useDebounced(async (value) => {
    if (!value.trim()) return;
    setSaveState('Saving…');
    try {
      await onUpdate(note.id, { body: value });
      setSaveState('Saved');
    } catch {
      setSaveState('Could not save');
    }
  }, 800);
  const legacy = note.id === 'legacy';
  return (
    <NoteCard legacy={legacy} data-testid='note-card'>
      <header>
        <span className='avatar'>{note.author?.initials || '·'}</span>
        <span className='who'>{legacy ? 'Appointment notes' : note.author?.name || 'Clinician'}</span>
        <span>{fmt(note.updated_at || note.created_at)}</span>
        {note.visibility === 'shared' && <span className='tag'>Shared</span>}
        {canEdit && (
          <span className='actions'>
            <IconButton label={editing ? 'Done editing' : 'Edit note'} variant='ghost' size={30} onClick={() => setEditing((e) => !e)}>
              <PencilIcon size={15} />
            </IconButton>
            <IconButton label='Delete note' variant='ghost' size={30} onClick={() => onDelete(note)}>
              <TrashIcon size={15} />
            </IconButton>
          </span>
        )}
      </header>
      {editing ? (
        <>
          <TextArea
            autoFocus
            value={text}
            maxLength={5000}
            onChange={(e) => {
              setText(e.target.value);
              save(e.target.value);
            }}
          />
          <small style={{ color: colors.textMuted }}>{saveState || 'Changes save automatically.'}</small>
        </>
      ) : (
        <p>{note.body}</p>
      )}
    </NoteCard>
  );
};

/**
 * NotesPanel - presentational drawer body. Use <NotesDrawer/> in the app (it wires the API).
 */
export const NotesPanel = ({
  notes = [],
  mode = 'notes',
  legacyText = '',
  status = 'ready',
  currentUserId,
  onCreate,
  onUpdate,
  onDelete,
  onSaveLegacy,
  onClose,
  patientName,
  draftKey = 'ibis-notes-draft',
  readOnly = false,
}) => {
  const [draft, setDraft] = useState(() => {
    try {
      return window.localStorage.getItem(draftKey) || '';
    } catch {
      return '';
    }
  });
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);
  const [legacy, setLegacy] = useState(legacyText);
  const [legacyState, setLegacyState] = useState('');
  const listRef = useRef(null);
  useEffect(() => setLegacy(legacyText), [legacyText]);
  useEffect(() => {
    try {
      if (draft) window.localStorage.setItem(draftKey, draft);
      else window.localStorage.removeItem(draftKey);
    } catch {
      /* storage unavailable */
    }
  }, [draft, draftKey]);
  useEffect(() => {
    listRef.current?.scrollTo?.({ top: listRef.current.scrollHeight });
  }, [notes.length]);

  const saveLegacy = useDebounced(async (value) => {
    setLegacyState('Saving…');
    try {
      await onSaveLegacy(value);
      setLegacyState('Saved');
    } catch {
      setLegacyState('Could not save - check your connection');
    }
  }, 900);

  const submit = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await onCreate(draft.trim(), shared ? 'shared' : 'clinician');
      setDraft('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Head>
        <DocumentIcon size={22} />
        <h2>
          Session Notes
          {patientName && <span className='sub'>Patient: {patientName}</span>}
        </h2>
        <IconButton label='Close notes' variant='ghost' size={36} onClick={onClose}>
          <CloseIcon size={20} />
        </IconButton>
      </Head>
      {mode === 'legacy' && readOnly ? (
        <List data-testid='notes-list'>
          <Empty>Notes are not available.</Empty>
        </List>
      ) : mode === 'legacy' ? (
        <Composer style={{ flex: 1, borderTop: 0 }}>
          <TextArea
            rows={14}
            style={{ flex: 1 }}
            placeholder='Type notes here…'
            value={legacy}
            onChange={(e) => {
              setLegacy(e.target.value);
              saveLegacy(e.target.value);
            }}
            aria-label='Session notes'
          />
          <span className='hint'>{legacyState || 'Notes save automatically.'}</span>
        </Composer>
      ) : (
        <>
          <List ref={listRef} data-testid='notes-list'>
            {status === 'loading' && <Empty>Loading notes…</Empty>}
            {status === 'error' && <Empty>Notes could not be loaded.</Empty>}
            {status === 'ready' && notes.length === 0 && (
              <Empty>
                <DocumentIcon size={36} />
                <div>
                  {readOnly
                    ? 'Your clinician has not shared any notes with you yet.'
                    : 'No notes yet. Anything you write here is saved to this appointment.'}
                </div>
              </Empty>
            )}
            {notes.map((n) => (
              <EditableNote
                key={n.id}
                note={n}
                canEdit={!readOnly && !n.read_only && n.id !== 'legacy' && (!currentUserId || n.author?.id === currentUserId)}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </List>
          {!readOnly && (
            <Composer>
              <TextArea
                rows={4}
                placeholder='Add a note…'
                value={draft}
                maxLength={5000}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
                }}
                aria-label='New note'
              />
              <div className='row'>
                <label>
                  <input type='checkbox' checked={shared} onChange={(e) => setShared(e.target.checked)} />
                  Share with patient
                </label>
                <Button onClick={submit} disabled={!draft.trim() || busy}>
                  {busy ? 'Saving…' : 'Save Note'}
                </Button>
              </div>
              <span className='hint'>Your draft is kept on this device until you save it.</span>
            </Composer>
          )}
        </>
      )}
    </>
  );
};

/**
 * NotesDrawer - connected drawer. Props: open, onClose, appointmentId, currentUserId, patientName,
 * readOnly (patient: lists the notes the clinician shared, no composer / edit).
 */
export const NotesDrawer = ({ open, onClose, appointmentId, currentUserId, patientName, readOnly = false }) => {
  const api = useSessionNotes(appointmentId, { enabled: open });
  const { confirm, toast } = useSessionUI();
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;

  const onCreate = async (body, visibility) => {
    try {
      await api.create(body, visibility);
    } catch (e) {
      toast({ tone: 'error', title: 'Note not saved', body: e.message });
      throw e;
    }
  };
  const onDelete = async (note) => {
    if (!(await confirm({ message: 'Delete this note?', yesLabel: 'Delete', noLabel: 'Cancel', tone: 'danger' }))) return;
    try {
      await api.remove(note.id);
    } catch (e) {
      toast({ tone: 'error', title: 'Note not deleted', body: e.message });
    }
  };

  return createPortal(
    <>
      <Backdrop onClick={onClose} />
      <Drawer role='dialog' aria-modal='true' aria-label='Session notes' data-testid='notes-drawer'>
        <NotesPanel
          notes={api.notes}
          mode={api.mode}
          legacyText={api.legacyText}
          status={api.status}
          currentUserId={currentUserId}
          onCreate={onCreate}
          onUpdate={api.update}
          onDelete={onDelete}
          onSaveLegacy={api.saveLegacy}
          onClose={onClose}
          patientName={patientName}
          draftKey={`ibis-notes-draft-${appointmentId}`}
          readOnly={readOnly}
        />
      </Drawer>
    </>,
    document.body,
  );
};

export default NotesDrawer;
